import { useLayoutStore } from "@/roc/stores/LayoutStore";
import { useWonderStore } from "@/roc/stores/WonderStore";
import PassiveAura from "@/roc/effects/PassiveAura/PassiveAura";
import PassiveAuraType from "@/roc/effects/PassiveAura/PassiveAuraType";
import EraType from "@/roc/era/EraType";
import { applyWonderSynergies } from "@/components/Bonuses/Calculator/Bonus";
const unitDefs = {
    bastion: 'Bastion',
    siege: 'Siege',
    heavy: 'Heavy Infantry',
    cavalry: 'Cavalry',
    ranged: 'Ranged',
    light: 'Light Infantry',
};
const statDefs = {
    recruitmentTime: 'Recruitment Time',
    speed: 'Movement Speed',
    haste: 'Hit Rate',
    critical: 'Critical Hit Chance',
    health: 'Hit Points',
    damage: 'Damage',
    critDamage: 'Critical Hit Damage',
};
const baseStats = {
    recruitmentTime: 0,
    speed: 0,
    haste: 0,
    critical: 0,
    health: 0,
    damage: 0,
    critDamage: 0,
};
const initStatsStructure = function () {
    const stats = {};
    for (const i in unitDefs) {
        stats[i] = {
            title: unitDefs[i],
            stats: { ...baseStats },
        };
    }
    return stats;
};
class ArmyBonusCalculator {
    static calculate(playerEra) {
        const lytStore = useLayoutStore();
        const wonderStore = useWonderStore();
        const stats = initStatsStructure();
        const result = [];
        const processEffect = function (effect, amount) {
            const formatSeconds = (seconds) => {
                seconds /= 60;
                const m = seconds % 60;
                const h = (seconds - m) / 60;
                if (m > 0) {
                    return (h > 0 ? h + 'h ' : '') + m + 'm';
                }
                else if (h === 24) {
                    return '1d';
                }
                return h + 'h';
            };
            switch (effect.type) {
                // Infantry
                case PassiveAuraType.INFANTRY_BARRACKS_RECRUITMENT_TIME_BONUS:
                    stats.light.stats.recruitmentTime += amount;
                    break;
                case PassiveAuraType.LIGHT_INFANTRY_HIT_POINT_MOD:
                    stats.light.stats.health += amount;
                    break;
                case PassiveAuraType.LIGHT_INFANTRY_DAMAGE_MOD:
                    stats.light.stats.damage += amount;
                    break;
                case PassiveAuraType.LIGHT_INFANTRY_CRITICAL_HIT_CHANCE_MOD:
                    stats.light.stats.critical += amount;
                    break;
                // Heavy Infantry
                case PassiveAuraType.HEAVY_INFANTRY_PRODUCTION_TIME_BONUS:
                    stats.heavy.stats.recruitmentTime += amount;
                    break;
                case PassiveAuraType.HEAVY_INFANTRY_HIT_POINT_MOD:
                    stats.heavy.stats.health += amount;
                    break;
                case PassiveAuraType.HEAVY_INFANTRY_DAMAGE_MOD:
                    stats.heavy.stats.damage += amount;
                    break;
                case PassiveAuraType.HEAVY_INFANTRY_CRITICAL_HIT_CHANCE_MOD:
                    stats.heavy.stats.critical += amount;
                    break;
                // Ranged
                case PassiveAuraType.RANGED_BARRACKS_RECRUITMENT_TIME_BONUS:
                    stats.ranged.stats.recruitmentTime += amount;
                    break;
                case PassiveAuraType.RANGED_HIT_POINT_MOD:
                    stats.ranged.stats.health += amount;
                    break;
                case PassiveAuraType.RANGED_DAMAGE_MOD:
                    stats.ranged.stats.damage += amount;
                    break;
                case PassiveAuraType.RANGED_CRITICAL_HIT_CHANCE_MOD:
                    stats.ranged.stats.critical += amount;
                    break;
                case PassiveAuraType.RANGED_CRITICAL_HIT_DAMAGE_MOD:
                    stats.ranged.stats.critDamage += amount;
                    break;
                // Cavalry
                case PassiveAuraType.CAVALRY_HIT_POINT_MOD:
                    stats.cavalry.stats.health += amount;
                    break;
                case PassiveAuraType.CAVALRY_DAMAGE_MOD:
                    stats.cavalry.stats.damage += amount;
                    break;
                case PassiveAuraType.CAVALRY_HIT_RATE_MOD:
                    stats.cavalry.stats.haste += amount;
                    break;
                case PassiveAuraType.CAVALRY_MOVEMENT_SPEED_MOD:
                    stats.cavalry.stats.speed += amount;
                    break;
                // Siege
                case PassiveAuraType.SIEGE_BARRACKS_RECRUITMENT_TIME_BONUS:
                    stats.siege.stats.recruitmentTime += amount;
                    break;
                case PassiveAuraType.SIEGE_HIT_POINT_MOD:
                    stats.siege.stats.health += amount;
                    break;
                case PassiveAuraType.SIEGE_DAMAGE_MOD:
                    stats.siege.stats.damage += amount;
                    break;
                // Bastion
                case PassiveAuraType.BASTION_HIT_POINT_MOD:
                    stats.bastion.stats.health += amount;
                    break;
                // All
                case PassiveAuraType.ALL_BARRACKS_RECRUITMENT_TIME_BONUS:
                    for (const j in stats) {
                        if (j !== 'bastion') {
                            stats[j].stats.recruitmentTime += amount;
                        }
                    }
                    break;
                case PassiveAuraType.TOTAL_HIT_POINT_MOD:
                    for (const j in stats) {
                        if (j !== 'bastion') {
                            stats[j].stats.health += amount;
                        }
                    }
                    break;
                case PassiveAuraType.TOTAL_DAMAGE_MOD:
                    for (const j in stats) {
                        if (j !== 'bastion') {
                            stats[j].stats.damage += amount;
                        }
                    }
                    break;
                case PassiveAuraType.AZTEC_TEMPLE_RECRUITMENT_TIME_BONUS:
                    result.push({
                        icon: 'CrocsBoost',
                        description: `Reduce recruitment time of the Aztec Crocodiles to ${formatSeconds(amount)}.`,
                    });
                    break;
                case PassiveAuraType.AZTEC_CROCODILE_DAMAGE_MOD:
                    result.push({
                        icon: 'AztecCrocodile',
                        overlay: 'StatDamage',
                        description: `Increase damage of the Aztec Crocodiles by ${amount}%.`,
                    });
                    break;
                case PassiveAuraType.IMPERIAL_GUARD_PRODUCTION_TIME_BONUS:
                    result.push({
                        icon: 'ImperialGuard',
                        overlay: 'StatRecruitmentTime',
                        description: `Reduce recruitment time of the Imperial Guard by ${amount}%.`,
                    });
                    break;
                case PassiveAuraType.MAYA_ARCHERS_PRODUCTION_TIME_BONUS:
                    result.push({
                        icon: 'MayaArcher',
                        overlay: 'StatRecruitmentTime',
                        description: `Reduce recruitment time of the Maya Archers by ${amount}%.`,
                    });
                    break;
                case PassiveAuraType.CARCASSONNE_SENTINELS_PRODUCTION_TIME_BONUS:
                    result.push({
                        icon: 'Carcassonne',
                        overlay: 'StatRecruitmentTime',
                        description: `Reduce recruitment time of the Carcassonne Sentinels by ${amount}%.`,
                    });
                    break;
            }
        };
        lytStore.mapBuildings((building, buildingId) => {
            for (let i = 0; i < building.effects.count(); i++) {
                const effect = building.effects.getEffect(i);
                if (effect instanceof PassiveAura) {
                    const amount = effect.getAmount(building.level || 1, building.era || playerEra);
                    if (!amount) {
                        continue;
                    }
                    processEffect(effect, amount);
                }
            }
            return true;
        });
        wonderStore.mapWonders((wonder, config) => {
            for (let i = 0; i < wonder.effects.count(); i++) {
                if (config.active) {
                    const effect = wonder.effects.getEffect(i);
                    if (effect instanceof PassiveAura) {
                        const amount = effect.getAmount(config.level, EraType.stoneAge);
                        if (amount) {
                            processEffect(effect, amount);
                            applyWonderSynergies(wonder, config.level, i, processEffect);
                        }
                    }
                }
            }
            return true;
        });
        for (const i in stats) {
            for (const j in stats[i].stats) {
                const value = stats[i].stats[j];
                if (value === 0) {
                    continue;
                }
                const overlay = 'Stat' + j.substring(0, 1).toUpperCase() + j.substring(1);
                const icon = stats[i].title.replaceAll(' ', '');
                const title = stats[i].title === 'Ranged' ? 'Archers' : stats[i].title;
                if (j === 'recruitmentTime') {
                    result.push({
                        icon,
                        overlay,
                        description: `Reduce <b>Recruitment Time</b> of <b>${title}</b> by <b>${value.toFixed(2)}%</b>.`,
                    });
                }
                else {
                    result.push({
                        icon,
                        overlay,
                        description: `Increase <b>${statDefs[j]}</b> of <b>${title}</b> by <b>${value.toFixed(2)}%</b>.`,
                    });
                }
            }
        }
        return result.reverse();
    }
}
export default ArmyBonusCalculator;
